import React, { useCallback, useEffect, useRef } from 'react';
import { PropTypes } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Draggable } from 'react-beautiful-dnd';
import uniqueId from 'lodash/uniqueId';

import { useDeleteAnswerMutation } from 'api/main';
import { customTunnelSelectors } from 'reducers';
import { editingAnswerIdSet } from 'reducers/customTunnel';
import { useDialog } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import AnswerForm from './AnswerForm';
import { ConfirmDialog } from 'components';
import { Box, Collapse, Paper, Typography } from '@mui/material';
import * as icons from 'mdi-material-ui';

const iconSx = { cursor: 'pointer' };

const AnswerItem = ({
    answer,
    onRemove,
    onUpdate,
    index,
    questionIndex,
    pmQuestions,
    formClear,
}) => {
    const uId = useRef(uniqueId()); // unique id used for editing state
    const dispatch = useDispatch();
    const editingAnswerId = useSelector(customTunnelSelectors.getEditingAnswerId);

    const { ppId: ppQuestionId } = useSelector(customTunnelSelectors.getEditingQuestionId);

    const [deleteAnswer, { isLoading: deleteLoading, isSuccess: deleteSuccessful }] =
        useDeleteAnswerMutation();

    const { open: confirmEditOpen, onClose: handleEditClose, onOpen: handleEditOpen } = useDialog();
    const { open: deleteOpen, onClose: handleDeleteClose, onOpen: handleDeleteOpen } = useDialog();

    const handleUpdateAnswer = useCallback(
        newAnswer => {
            onUpdate(newAnswer);
            dispatch(editingAnswerIdSet(null));
        },
        [onUpdate, dispatch]
    );

    const handleEdit = useCallback(() => {
        dispatch(editingAnswerIdSet(uId.current));
        // close the confirm edit dialog dialog
        handleEditClose();
        // clear the empty answers in the form of the parent component
        formClear();
    }, [dispatch, formClear, handleEditClose]);

    const handleConfirmEdit = useCallback(() => {
        if (editingAnswerId) {
            handleEditOpen();
        } else {
            handleEdit();
        }
    }, [editingAnswerId, handleEdit, handleEditOpen]);

    const handleCancel = useCallback(() => {
        if (!answer.title) {
            // remove answer from the parent component form state
            onRemove();
        }
        // cancel editing answer
        dispatch(editingAnswerIdSet(null));
    }, [answer, onRemove, dispatch]);

    const handleDeleteAnswer = useCallback(() => {
        if (answer?.id) {
            // update existing answer in the DB
            deleteAnswer({ ppId: answer?.id, pmId: pmQuestions[questionIndex].answers[index].id });
        } else if (ppQuestionId) {
            // remove from question from state (question exists in DB)
        } else {
            // remove from new step form state (step doesn't exist in DB)
            onRemove();
        }
    }, [answer?.id, ppQuestionId, deleteAnswer, pmQuestions, questionIndex, index, onRemove]);

    useEffect(() => {
        if (answer.title) {
            dispatch(editingAnswerIdSet(null));
        } else {
            dispatch(editingAnswerIdSet(uId.current));
        }
    }, [dispatch, uId, answer]);

    useEffect(() => {
        if (deleteSuccessful) {
            handleDeleteClose();
        }
    }, [deleteSuccessful, handleDeleteClose]);

    const pmAnswerId = pmQuestions?.[questionIndex]?.answers?.[index]?.id;

    return (
        <Draggable
            draggableId={`${answer?.id || ''}-${pmAnswerId}-answer-${uId.current}`}
            index={index}
            isDragDisabled={editingAnswerId === uId.current}
        >
            {(provided, snapshot) => (
                <Paper
                    {...provided.draggableProps}
                    ref={provided.innerRef}
                    sx={theme => ({
                        padding: 1,
                        borderRadius: '3px',
                        border:
                            editingAnswerId === uId.current
                                ? `2px solid ${theme.palette.grey[500]}`
                                : '',
                        backgroundColor:
                            editingAnswerId !== uId.current ? theme.palette.grey[200] : 'initial',
                        ...provided.draggableProps.style,
                        opacity: snapshot.isDragging ? '.75' : '1',
                    })}
                    elevation={3}
                >
                    {editingAnswerId !== uId.current && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    maxWidth: '85%',
                                    gap: 1,
                                    flex: '1 1',
                                }}
                            >
                                <Typography title={answer.title} noWrap sx={{ maxWidth: '90%' }}>
                                    {answer.title}
                                </Typography>
                                <Typography noWrap sx={{ maxWidth: '10%' }}>
                                    {' '}
                                    : {answer.score}
                                </Typography>
                            </Box>
                            <Box display="flex" alignItems="center">
                                <ConfirmDialog
                                    open={deleteOpen}
                                    handleConfirm={handleDeleteAnswer}
                                    handleClose={handleDeleteClose}
                                    title={texts.DELETE_ANSWER}
                                    loading={deleteLoading}
                                    confirmText={texts.CONFIRMER}
                                    cancelText={texts.CANCEL}
                                >
                                    {texts.CONFIRM_DELETE_ANSWER}
                                </ConfirmDialog>
                                <ConfirmDialog
                                    open={confirmEditOpen}
                                    handleConfirm={handleEdit}
                                    handleClose={handleEditClose}
                                    title={texts.CHANGES_NOT_CONFIRMED}
                                    confirmText={texts.CONFIRMER}
                                    cancelText={texts.CANCEL}
                                >
                                    {texts.CONFIRM_DISCARD_CHANGES}
                                </ConfirmDialog>
                                <icons.Pencil
                                    sx={{ ...iconSx, '&:hover': { color: 'primary.main' } }}
                                    onClick={handleConfirmEdit}
                                />
                                <icons.Delete
                                    sx={{ ...iconSx, '&:hover': { color: 'error.main' } }}
                                    onClick={handleDeleteOpen}
                                />
                                <span {...provided.dragHandleProps} style={{ display: 'flex' }}>
                                    <icons.Drag
                                        sx={{
                                            ...iconSx,
                                            cursor: 'move',
                                            ':hover': { color: 'initial' },
                                        }}
                                    />
                                </span>
                            </Box>
                        </Box>
                    )}
                    <Collapse in={editingAnswerId === uId.current}>
                        {editingAnswerId === uId.current && ( // need to unmount children when closed
                            <AnswerForm
                                answer={answer}
                                onUpdate={handleUpdateAnswer}
                                onCancel={handleCancel}
                                pmAnswerId={pmAnswerId}
                                index={index}
                            />
                        )}
                    </Collapse>
                </Paper>
            )}
        </Draggable>
    );
};

AnswerItem.propTypes = {
    answer: PropTypes.object,
    onRemove: PropTypes.func,
    onUpdate: PropTypes.func,
    formClear: PropTypes.func,
    index: PropTypes.number,
    questionIndex: PropTypes.number,
    pmQuestions: PropTypes.array,
};

export default AnswerItem;
